/**
 * @file Notification service.
 * @description Manages app-wide toast-style notifications including token-expiry prompts.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
/**
 * Notification service for handling token expiration and other notifications
 */
class NotificationService {
  private notificationContainer: HTMLElement | null = null;
  private currentNotification: HTMLElement | null = null;
  private clearTimer: number | null = null;

  /**
   * Initialize the notification service
   */
  init(): void {
    // Create notification container if it doesn't exist
    if (!document.getElementById('notification-container')) {
      const container = document.createElement('div');
      container.id = 'notification-container';
      container.className = 'fixed top-4 right-4 z-50';
      document.body.appendChild(container);
      this.notificationContainer = container;
    } else {
      this.notificationContainer = document.getElementById('notification-container');
    }
  }

  /**
   * Show a token expired notification
   */
  showTokenExpired(message?: string): void {
    const errorMessage = message || 'Your session has expired. Please log in again.';
    
    // Remove existing notification
    this.clearNotification();
    
    if (!this.notificationContainer) {
      this.init();
    }

    const notification = document.createElement('div');
    notification.className = `
      bg-red-50 border border-red-200 rounded-lg p-4 mb-4 shadow-lg
      animate-in slide-in-from-right duration-300
    `;
    
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-medium text-red-800">
            Session Expired
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <p>${errorMessage}</p>
            <p class="mt-1">You will be redirected to the login page automatically.</p>
          </div>
        </div>
        <div class="ml-4 flex-shrink-0">
          <button onclick="this.parentElement.parentElement.parentElement.remove()" 
                  class="bg-red-50 rounded-md inline-flex text-red-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
            <span class="sr-only">Close</span>
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `;

    this.notificationContainer?.appendChild(notification);
    this.currentNotification = notification;

    // Auto-remove after 5 seconds
    this.clearTimer = window.setTimeout(() => {
      this.clearNotification();
    }, 5000);
  }

  /**
   * Clear the current notification
   */
  clearNotification(): void {
    if (this.clearTimer) {
      window.clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }

    if (this.currentNotification) {
      this.currentNotification.remove();
      this.currentNotification = null;
    }
  }

  /**
   * Show a success notification
   */
  showSuccess(message: string): void {
    this.showNotification(message, 'success');
  }

  /**
   * Show an error notification
   */
  showError(message: string): void {
    this.showNotification(message, 'error');
  }

  /**
   * Show a warning notification
   */
  showWarning(message: string): void {
    this.showNotification(message, 'warning');
  }

  /**
   * Show a generic notification
   */
  private showNotification(message: string, type: 'success' | 'error' | 'warning'): void {
    this.clearNotification();
    
    if (!this.notificationContainer) {
      this.init();
    }

    const colors = {
      success: 'bg-green-50 border-green-200 text-green-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    };

    const icons = {
      success: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
      error: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
      warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
    };

    const notification = document.createElement('div');
    notification.className = `
      ${colors[type]} border rounded-lg p-4 mb-4 shadow-lg
      animate-in slide-in-from-right duration-300
    `;
    
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="${icons[type]}" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <div class="ml-4 flex-shrink-0">
          <button onclick="this.parentElement.parentElement.parentElement.remove()" 
                  class="rounded-md inline-flex hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2">
            <span class="sr-only">Close</span>
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `;

    this.notificationContainer?.appendChild(notification);
    this.currentNotification = notification;

    // Auto-remove after 3 seconds
    this.clearTimer = window.setTimeout(() => {
      this.clearNotification();
    }, 3000);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Global function for easy access
export const showTokenExpiredNotification = (message?: string) => {
  notificationService.showTokenExpired(message);
};
